import type {
  InventoryFare,
  InventoryFareBasis,
  InventoryFlight,
  InventoryItinerary,
  InventorySegment,
  MoneyAmount,
} from '../api/inventory'

const AIRLINE_NAMES: Record<string, string> = {
  AA: 'American Airlines',
  AC: 'Air Canada',
  AF: 'Air France',
  BA: 'British Airways',
  DL: 'Delta Air Lines',
  LH: 'Lufthansa',
  TK: 'Turkish Airlines',
  UA: 'United Airlines',
  VS: 'Virgin Atlantic',
}

const AIRPORT_CITIES: Record<string, string> = {
  CDG: 'Paris',
  CLT: 'Charlotte',
  IST: 'Istanbul',
  JFK: 'New York',
  LAX: 'Los Angeles',
  LHR: 'London',
  ORD: 'Chicago',
  PHL: 'Philadelphia',
  RMO: 'Chisinau',
  SFO: 'San Francisco',
  YUL: 'Montreal',
  YVR: 'Vancouver',
  YYZ: 'Toronto',
}

export function parseMoney(value: unknown): MoneyAmount | null {
  if (!value || typeof value !== 'object') return null
  const money = value as { amount?: number | string; currency?: string }
  const amount = Number(money.amount)
  if (!Number.isFinite(amount)) return null
  return { amount, currency: money.currency || 'CAD' }
}

export function formatMoney(value: unknown): string {
  const money = parseMoney(value)
  if (!money) return '—'
  const symbol = money.currency === 'USD' || money.currency === 'CAD' ? '$' : money.currency
  return `${symbol} ${money.amount.toFixed(2)}`
}

export function fareTotal(fare: InventoryFare | null | undefined): MoneyAmount | null {
  if (!fare) return null
  const total = parseMoney(fare.total_amount)
  if (total) return total
  const base = parseMoney(fare.base_amount)
  const taxes = parseMoney(fare.total_taxes)
  if (!base && !taxes) return null
  return {
    amount: (base?.amount ?? 0) + (taxes?.amount ?? 0),
    currency: base?.currency ?? taxes?.currency ?? 'CAD',
  }
}

export function formatClockTime(value: string | null | undefined): string {
  if (!value) return '—'
  const datetimeMatch = String(value).match(/[T ](\d{1,2}):(\d{2})/)
  if (datetimeMatch) {
    return `${datetimeMatch[1].padStart(2, '0')}:${datetimeMatch[2]}`
  }
  const match = String(value).match(/^(\d{1,2}):(\d{2})/)
  if (!match) return String(value)
  return `${match[1].padStart(2, '0')}:${match[2]}`
}

export function clockTimeFromDatetime(value: string | null | undefined): string {
  return formatClockTime(value)
}

export function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

const WEEKDAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export function toDateInput(value: string | null | undefined): string {
  const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

export function toDateTimeLocalInput(value: string | null | undefined): string {
  if (!value) return ''
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/)
  if (match) return `${match[1]}T${match[2]}`
  const dateOnly = toDateInput(value)
  return dateOnly ? `${dateOnly}T00:00` : ''
}

export function weekdayFromDate(dateValue: string) {
  const date = parseIsoDate(dateValue)
  if (!date) return null
  return WEEKDAY_CODES[date.getDay()]
}

/**
 * The one display format for dates across the app: MM/DD/YYYY HH:mm, read
 * straight from the wall-clock string (no timezone conversion). A date-only
 * value shows 00:00.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/)
  if (!match) {
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return String(value)
    const mm = String(parsed.getMonth() + 1).padStart(2, '0')
    const dd = String(parsed.getDate()).padStart(2, '0')
    const hh = String(parsed.getHours()).padStart(2, '0')
    const mi = String(parsed.getMinutes()).padStart(2, '0')
    return `${mm}/${dd}/${parsed.getFullYear()} ${hh}:${mi}`
  }
  const [, year, month, day, hours = '00', minutes = '00'] = match
  return `${month}/${day}/${year} ${hours}:${minutes}`
}

/** MM/DD/YYYY without a time, for values that are genuinely dates (e.g. date of birth). */
export function formatDateOnly(value: string | null | undefined): string {
  const full = formatDateTime(value)
  return full === '—' ? full : full.slice(0, 10)
}

export function formatIsoDate(value: string | null | undefined): string {
  const date = parseIsoDate(value)
  if (!date) return '—'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function formatShortDate(value: string | null | undefined): string {
  const date = parseIsoDate(value)
  if (!date) return '—'
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

export function formatTicketBy(value: string | null | undefined): string {
  const date = parseIsoDate(value)
  if (!date) return '—'
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function formatDays(value: string | null | undefined): string {
  const days = String(value || '')
    .split(',')
    .map((day) => day.trim().toUpperCase())
    .filter(Boolean)
  return days.length ? days.join(', ') : '—'
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return '—'
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  if (hours && mins) return `${hours}h ${mins}m`
  if (hours) return `${hours}h`
  return `${mins}m`
}

export function dayOffset(flight: InventoryFlight): number {
  const depDate = parseIsoDate(flight.departure_datetime)
  const arrDate = parseIsoDate(flight.arrival_datetime)
  if (!depDate || !arrDate) return 0
  const msPerDay = 24 * 60 * 60 * 1000
  const depDay = Date.UTC(depDate.getFullYear(), depDate.getMonth(), depDate.getDate())
  const arrDay = Date.UTC(arrDate.getFullYear(), arrDate.getMonth(), arrDate.getDate())
  return Math.max(0, Math.round((arrDay - depDay) / msPerDay))
}

export function addDays(value: string, days: number): string {
  const date = parseIsoDate(value)
  if (!date) return value
  date.setDate(date.getDate() + days)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function airlineName(code: string | null | undefined): string {
  if (!code) return '—'
  return AIRLINE_NAMES[code.toUpperCase()] ?? code.toUpperCase()
}

export function airportLabel(code: string | null | undefined): string {
  if (!code) return '—'
  const city = AIRPORT_CITIES[code.toUpperCase()]
  return city ? `${city} (${code.toUpperCase()})` : code.toUpperCase()
}

export function segmentFlights(segment: InventorySegment | null | undefined): InventoryFlight[] {
  return [...(segment?.flights ?? [])].sort(
    (a, b) => (a.flight_order ?? 0) - (b.flight_order ?? 0),
  )
}

export function outboundSegment(row: InventoryItinerary): InventorySegment | null {
  if (row.outbound) return row.outbound
  const segments = [...(row.segments ?? [])].sort(
    (a, b) => a.segment_order - b.segment_order,
  )
  return (
    segments.find((segment) => segment.direction === 'outbound') ??
    segments[0] ??
    null
  )
}

export function inboundSegment(row: InventoryItinerary): InventorySegment | null {
  if (row.inbound) return row.inbound
  const segments = [...(row.segments ?? [])].sort(
    (a, b) => a.segment_order - b.segment_order,
  )
  return (
    segments.find((segment) => segment.direction === 'inbound') ??
    (segments.length > 1 ? segments[1] ?? null : null)
  )
}

export function routeSummary(segment: InventorySegment | null | undefined): {
  origin: string
  destination: string
  connections: number
} {
  const flights = segmentFlights(segment)
  if (!flights.length) {
    return { origin: '—', destination: '—', connections: 0 }
  }
  return {
    origin: flights[0].departure_airport,
    destination: flights[flights.length - 1].arrival_airport,
    connections: Math.max(0, flights.length - 1),
  }
}

export function fareBasisForFlight(
  row: InventoryItinerary,
  flight: InventoryFlight,
): InventoryFareBasis | null {
  return (
    flight.fare_basis ??
    row.fare_basis?.find((item) => item.flight_id === flight.flight_id) ??
    null
  )
}

export function segmentCabin(
  row: InventoryItinerary,
  segment: InventorySegment | null | undefined,
): string {
  const flights = segmentFlights(segment)
  const cabins = Array.from(
    new Set(
      flights
        .map((flight) => fareBasisForFlight(row, flight)?.cabin)
        .filter((cabin): cabin is string => Boolean(cabin)),
    ),
  )
  return cabins[0] || '—'
}

function datetimeMs(value: string | null | undefined): number | null {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
    ).getTime()
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime()
}

export function layoverMinutes(
  previous: InventoryFlight,
  next: InventoryFlight,
): number | null {
  const arrivalMs = datetimeMs(previous.arrival_datetime)
  const departureMs = datetimeMs(next.departure_datetime)
  if (arrivalMs == null || departureMs == null) return null
  const diff = Math.round((departureMs - arrivalMs) / 60_000)
  return Number.isFinite(diff) ? diff : null
}

export function totalFlightMinutes(flights: InventoryFlight[]): number {
  return flights.reduce((sum, flight) => sum + (flight.duration_min || 0), 0)
}

export function totalTripMinutes(flights: InventoryFlight[]): number | null {
  if (flights.length < 1) return null
  const first = flights[0]
  const last = flights[flights.length - 1]
  const startMs = datetimeMs(first.departure_datetime)
  const endMs = datetimeMs(last.arrival_datetime)
  if (startMs == null || endMs == null) {
    return totalFlightMinutes(flights)
  }
  const diff = Math.round((endMs - startMs) / 60_000)
  return diff >= 0 ? diff : totalFlightMinutes(flights)
}
